/* Patırtı collection + search pages (Faz B2).
   Shopify Storefront Filtering forms → Section Rendering API (?section_id=) with URL/back-button sync,
   quick filter chips, sort, mobile filter drawer tabs, infinite loading with a link fallback,
   scroll restore after returning from a product, and the mobile "4 / 851" position counter. */
(function () {
  'use strict';
  if (customElements.get('plp-root')) return;

  const STATE_KEY = 'patirti_plp_state';
  const MAX_CACHE = 20;
  const lower = value => String(value || '').toLocaleLowerCase('tr-TR');
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;
  const sameOrigin = href => {
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin ? url : null;
    } catch (error) { return null; }
  };
  const cleanParams = source => {
    const out = new URLSearchParams();
    source.forEach((value, key) => {
      if (key === 'page' || key === 'section_id' || key === 'sections') return;
      if (String(value).trim() === '') return;
      out.append(key, value);
    });
    return out;
  };
  const paramsKey = params => Array.from(cleanParams(params)).map(pair => pair.join('=')).sort().join('&');
  const hasPair = (params, key, value) => params.getAll(key).includes(value);
  const removePair = (params, key, value) => {
    const rest = params.getAll(key).filter(item => item !== value);
    params.delete(key);
    rest.forEach(item => params.append(key, item));
  };
  const headerOffset = () => {
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    return Number.isFinite(value) ? value : 0;
  };

  class PlpRoot extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      if (!this.sectionId) return;
      this.cache = new Map();
      this.sequence = 0;
      this.cleanups = [];
      const on = (target, name, handler, options) => {
        target.addEventListener(name, handler, options);
        this.cleanups.push(() => target.removeEventListener(name, handler, options));
      };
      on(this, 'change', event => this.onChange(event));
      on(this, 'submit', event => this.onSubmit(event));
      on(this, 'click', event => this.onClick(event));
      on(this, 'input', event => this.onInput(event));
      on(this, 'keydown', event => this.onKeydown(event));
      on(window, 'popstate', () => this.onPopState());
      on(window, 'scroll', () => this.onScroll(), { passive: true });
      this.renderedKey = paramsKey(new URLSearchParams(location.search));
      const pagination = this.querySelector('[data-plp-pagination]');
      this.startPage = Number(pagination && pagination.dataset.currentPage) || 1;
      this.classList.add('is-enhanced');
      this.setupResults();
      this.syncControls();
      this.restoreScroll();
    }

    disconnectedCallback() {
      (this.cleanups || []).forEach(fn => fn());
      this.cleanups = [];
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      clearTimeout(this.debounce);
      clearTimeout(this.counterTimer);
      if (this.counterFrame) cancelAnimationFrame(this.counterFrame);
    }

    /* ---------- events ---------- */
    onChange(event) {
      const target = event.target;
      if (target.matches('[data-sort-select]')) { this.setSort(target.value); return; }
      if (target.matches('[data-sort-radio]')) {
        this.setSort(target.value);
        const drawer = target.closest('drawer-panel');
        if (drawer && typeof drawer.close === 'function') drawer.close();
        return;
      }
      const form = target.closest('[data-facet-form]');
      if (!form || !target.name || target.matches('[data-facet-search], [data-price-input]')) return;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.apply(this.paramsFromForm(form)), 250);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-facet-form]');
      if (!form) return;
      event.preventDefault();
      clearTimeout(this.debounce);
      this.normalizePrice(form);
      this.apply(this.paramsFromForm(form));
    }

    onClick(event) {
      const target = event.target;
      const chip = target.closest('[data-quick-filter]');
      if (chip) { event.preventDefault(); this.apply(this.quickTarget(new URLSearchParams(chip.dataset.params || ''))); return; }
      const link = target.closest('a[data-facet-link]');
      if (link) {
        const url = sameOrigin(link.href);
        if (url && url.pathname === location.pathname) { event.preventDefault(); this.apply(url.searchParams); }
        return;
      }
      const range = target.closest('[data-price-range]');
      if (range) {
        event.preventDefault();
        const form = range.closest('[data-facet-form]');
        if (!form) return;
        const min = form.querySelector('[data-price-input="min"]'), max = form.querySelector('[data-price-input="max"]');
        const wasActive = range.getAttribute('aria-pressed') === 'true';
        if (min) min.value = wasActive ? '' : range.dataset.min || '';
        if (max) max.value = wasActive ? '' : range.dataset.max || '';
        this.apply(this.paramsFromForm(form));
        return;
      }
      const tab = target.closest('[data-facet-tab]');
      if (tab) { this.selectTab(tab.dataset.facetTab); return; }
      const opener = target.closest('[data-facet-open]');
      if (opener) { this.selectTab(opener.dataset.facetOpen); return; }
      const more = target.closest('[data-load-more]');
      if (more) { event.preventDefault(); this.loadMore(); return; }
      const done = target.closest('[data-facet-done]');
      if (done) {
        const form = done.closest('[data-facet-form]');
        if (form) {
          this.normalizePrice(form);
          const params = this.paramsFromForm(form);
          if (paramsKey(params) !== paramsKey(new URLSearchParams(location.search))) this.apply(params);
        }
        this.scrollToResults(false);
        return;
      }
      if (target.closest('[data-product-grid] a, [data-product-grid] [data-product-card]')) this.saveScroll();
    }

    onInput(event) {
      const input = event.target;
      if (!input.matches('[data-facet-search]')) return;
      const query = lower(input.value.trim());
      const list = input.parentElement && input.parentElement.querySelector('.facet-list');
      if (!list) return;
      list.querySelectorAll('[data-facet-item]').forEach(item => {
        item.hidden = Boolean(query) && !lower(item.textContent).includes(query);
      });
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-facet-tab]');
      if (tab && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        const tabs = Array.from(tab.parentElement.querySelectorAll('[data-facet-tab]'));
        const index = tabs.indexOf(tab);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 :
          (index + (event.key === 'ArrowDown' ? 1 : -1) + tabs.length) % tabs.length;
        event.preventDefault();
        this.selectTab(tabs[next].dataset.facetTab);
        tabs[next].focus();
        return;
      }
      if (event.key === 'Enter' && event.target.matches('[data-facet-search]')) event.preventDefault();
    }

    onPopState() {
      const key = paramsKey(new URLSearchParams(location.search));
      if (key === this.renderedKey) return;
      this.apply(new URLSearchParams(location.search), false);
    }

    /* ---------- state helpers ---------- */
    paramsFromForm(form) {
      const params = new URLSearchParams();
      const ownedKeys = new Set(), ownedPairs = new Set();
      Array.from(form.elements).forEach(element => {
        if (!element.name) return;
        if (element.type === 'checkbox' || element.type === 'radio') ownedPairs.add(element.name + '\u0000' + element.value);
        else ownedKeys.add(element.name);
      });
      new FormData(form).forEach((value, key) => { if (typeof value === 'string') params.append(key, value); });
      // Keep URL params the form does not represent (quick-filter values, filters without an option here).
      new URLSearchParams(location.search).forEach((value, key) => {
        if (key === 'page' || ownedKeys.has(key) || ownedPairs.has(key + '\u0000' + value)) return;
        params.append(key, value);
      });
      return cleanParams(params);
    }

    normalizePrice(form) {
      const min = form.querySelector('[data-price-input="min"]'), max = form.querySelector('[data-price-input="max"]');
      if (!min || !max) return;
      const read = input => {
        const value = input.value.trim();
        if (value === '') return null;
        const number = Math.floor(Number(value.replace(',', '.')));
        return Number.isFinite(number) && number >= 0 ? number : null;
      };
      let low = read(min), high = read(max);
      if (low !== null && high !== null && low > high) { const swap = low; low = high; high = swap; }
      min.value = low === null ? '' : String(low);
      max.value = high === null ? '' : String(high);
    }

    quickActive(chipParams, current) {
      let any = false, all = true;
      chipParams.forEach((value, key) => { any = true; if (!hasPair(current, key, value)) all = false; });
      return any && all;
    }

    quickTarget(chipParams) {
      const current = new URLSearchParams(location.search);
      const active = this.quickActive(chipParams, current);
      chipParams.forEach((value, key) => {
        if (active) {
          if (key === 'sort_by') current.delete(key); else removePair(current, key, value);
        } else if (key === 'sort_by' || key === 'q') {
          current.set(key, value);
        } else if (!hasPair(current, key, value)) {
          current.append(key, value);
        }
      });
      current.delete('page');
      return current;
    }

    setSort(value) {
      const params = new URLSearchParams(location.search);
      if (value) params.set('sort_by', value); else params.delete('sort_by');
      this.apply(params);
    }

    selectTab(key) {
      const tabs = this.querySelectorAll('[data-facet-tab]');
      if (!tabs.length) return;
      let found = false;
      tabs.forEach(tab => {
        const selected = tab.dataset.facetTab === key;
        if (selected) found = true;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      if (!found) { tabs[0].setAttribute('aria-selected', 'true'); tabs[0].tabIndex = 0; key = tabs[0].dataset.facetTab; }
      this.querySelectorAll('[data-facet-panel]').forEach(panel => { panel.hidden = panel.dataset.facetPanel !== key; });
      const panels = this.querySelector('.facet-drawer__panels');
      if (panels) panels.scrollTop = 0;
    }

    syncControls() {
      const current = new URLSearchParams(location.search);
      this.querySelectorAll('[data-quick-filter]').forEach(chip => {
        const chipParams = new URLSearchParams(chip.dataset.params || '');
        const active = this.quickActive(chipParams, current);
        chip.classList.toggle('is-active', active);
        if (active) chip.setAttribute('aria-current', 'true'); else chip.removeAttribute('aria-current');
        const query = cleanParams(this.quickTarget(chipParams)).toString();
        chip.setAttribute('href', location.pathname + (query ? '?' + query : ''));
      });
      const sort = current.get('sort_by') || this.dataset.defaultSort || '';
      this.querySelectorAll('[data-sort-select]').forEach(select => {
        if (Array.from(select.options).some(option => option.value === sort)) select.value = sort;
      });
      this.querySelectorAll('[data-sort-radio]').forEach(radio => { radio.checked = radio.value === sort; });
      this.querySelectorAll('[data-sort-hidden]').forEach(input => { if (sort) input.value = sort; });
    }

    /* ---------- rendering ---------- */
    sectionUrl(url) {
      const target = new URL(url, location.origin);
      target.searchParams.set('section_id', this.sectionId);
      return target.pathname + target.search;
    }

    fetchSection(url) {
      if (this.cache.has(url)) return Promise.resolve(this.cache.get(url));
      if (this.controller) this.controller.abort();
      const controller = this.controller = new AbortController();
      let timedOut = false;
      const timer = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
      return fetch(this.sectionUrl(url), { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then(response => { if (!response.ok) throw new Error('HTTP ' + response.status); return response.text(); })
        .then(html => {
          this.cache.set(url, html);
          if (this.cache.size > MAX_CACHE) this.cache.delete(this.cache.keys().next().value);
          return html;
        })
        .catch(error => { if (timedOut) throw new Error('timeout'); throw error; })
        .finally(() => clearTimeout(timer));
    }

    async apply(params, push = true) {
      const query = cleanParams(params).toString();
      const url = location.pathname + (query ? '?' + query : '');
      const id = ++this.sequence;
      this.setBusy(true);
      try {
        const html = await this.fetchSection(url);
        if (id !== this.sequence) return;
        if (push && url !== location.pathname + location.search) history.pushState({ plp: this.sectionId }, '', url);
        this.renderedKey = paramsKey(new URLSearchParams(query));
        this.render(html);
      } catch (error) {
        if (id !== this.sequence || error.name === 'AbortError') return;
        location.assign(url);
      } finally {
        if (id === this.sequence) this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.classList.toggle('is-loading', busy);
      const results = this.querySelector('[data-plp-part="results"]');
      if (!results) return;
      if (busy) results.setAttribute('aria-busy', 'true'); else results.removeAttribute('aria-busy');
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const active = document.activeElement;
      const focusHolder = active && active.closest ? active.closest('[data-focus-key]') : null;
      const focusKey = focusHolder && this.contains(focusHolder) ? focusHolder.dataset.focusKey : null;
      doc.querySelectorAll('[data-plp-part]').forEach(fresh => {
        const name = fresh.getAttribute('data-plp-part');
        const current = this.querySelector('[data-plp-part="' + CSS.escape(name) + '"]');
        if (!current) return;
        // Keep the option list the shopper is interacting with (focus, typed price, search text).
        if (name.indexOf('facet-') === 0 && current.contains(active)) return;
        current.innerHTML = fresh.innerHTML;
        current.hidden = fresh.hidden;
      });
      if (focusKey && !this.contains(document.activeElement)) {
        const target = this.querySelector('[data-focus-key="' + CSS.escape(focusKey) + '"]');
        if (target) target.focus({ preventScroll: true });
      }
      this.setupResults();
      this.syncControls();
      this.announce();
      document.dispatchEvent(new CustomEvent('collection:reloaded', { detail: { sectionId: this.sectionId } }));
      if (!this.querySelector('drawer-panel[open]')) this.scrollToResults(true);
    }

    announce() {
      const status = this.querySelector('[data-plp-status]');
      const count = this.querySelector('[data-plp-part="count-drawer"]');
      const template = this.dataset.statusTemplate;
      if (!status || !template) return;
      const value = count ? count.textContent.trim() : String(this.querySelectorAll('[data-product-grid] > *').length);
      status.textContent = template.replace('__COUNT__', value);
    }

    scrollToResults(onlyIfAbove) {
      const target = this.querySelector('.plp-main') || this;
      const top = target.getBoundingClientRect().top - headerOffset() - 8;
      if (onlyIfAbove && top >= 0) return;
      window.scrollTo({ top: Math.max(0, window.scrollY + top), behavior: reduced() ? 'auto' : 'smooth' });
    }

    /* ---------- infinite loading ---------- */
    setupResults() {
      if (this.observer) { this.observer.disconnect(); this.observer = null; }
      this.grid = this.querySelector('[data-product-grid]');
      const more = this.querySelector('[data-load-more]');
      if (!this.grid || !more || this.dataset.infinite !== 'true' || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.loadMore();
      }, { rootMargin: '0px 0px 800px 0px' });
      this.observer.observe(more);
    }

    async loadMore() {
      const link = this.querySelector('[data-load-more]');
      if (!link || this.loadingMore || !this.grid) return false;
      const url = sameOrigin(link.getAttribute('href'));
      if (!url) return false;
      this.loadingMore = true;
      const sequence = this.sequence;
      link.setAttribute('aria-busy', 'true');
      link.classList.add('is-loading');
      try {
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url.pathname + url.search, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (sequence !== this.sequence || !this.grid || !this.grid.isConnected) return false;
        const seen = new Set(Array.from(this.grid.children).map(node => node.dataset.itemId).filter(Boolean));
        const fragment = document.createDocumentFragment();
        let added = 0;
        doc.querySelectorAll('[data-product-grid] > [data-item-id]').forEach(item => {
          const id = item.dataset.itemId;
          if (seen.has(id)) return;
          seen.add(id);
          fragment.appendChild(document.importNode(item, true));
          added += 1;
        });
        this.grid.appendChild(fragment);
        this.grid.dataset.loadedCount = String(this.grid.children.length);
        const fresh = doc.querySelector('[data-plp-pagination]');
        const current = this.querySelector('[data-plp-pagination]');
        if (current) {
          if (fresh) {
            current.innerHTML = fresh.innerHTML;
            current.dataset.currentPage = fresh.dataset.currentPage || '';
            current.dataset.pages = fresh.dataset.pages || '';
          } else {
            const stale = current.querySelector('[data-load-more]');
            if (stale) stale.remove();
          }
        }
        document.dispatchEvent(new CustomEvent('collection:reloaded', { detail: { sectionId: this.sectionId, appended: added } }));
        this.setupResults();
        return true;
      } catch (error) {
        if (this.observer) { this.observer.disconnect(); this.observer = null; }
        const label = link.querySelector('[data-load-more-label]');
        if (label && this.dataset.loadError) label.textContent = this.dataset.loadError;
        return false;
      } finally {
        this.loadingMore = false;
        link.removeAttribute('aria-busy');
        link.classList.remove('is-loading');
      }
    }

    /* ---------- return-to-position after visiting a product ---------- */
    saveScroll() {
      const pagination = this.querySelector('[data-plp-pagination]');
      try {
        sessionStorage.setItem(STATE_KEY, JSON.stringify({
          url: location.href,
          page: Number(pagination && pagination.dataset.currentPage) || this.startPage,
          y: Math.round(window.scrollY),
          at: Date.now()
        }));
      } catch (error) { /* Storage can be unavailable. */ }
    }

    async restoreScroll() {
      let state = null;
      try { state = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null'); } catch (error) { state = null; }
      const navigation = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
      if (!state || state.url !== location.href || !navigation || navigation.type !== 'back_forward' || Date.now() - state.at > 1800000) return;
      try { sessionStorage.removeItem(STATE_KEY); } catch (error) { /* ignore */ }
      let page = this.startPage, guard = 0;
      while (page < state.page && guard < 12) {
        guard += 1;
        const loaded = await this.loadMore();
        if (!loaded) break;
        const pagination = this.querySelector('[data-plp-pagination]');
        page = Number(pagination && pagination.dataset.currentPage) || page + 1;
      }
      requestAnimationFrame(() => window.scrollTo(0, state.y));
    }

    /* ---------- mobile position counter ---------- */
    onScroll() {
      if (this.counterFrame || isDesktop()) return;
      const counter = this.querySelector('[data-position-counter]');
      if (!counter || !this.grid) return;
      this.counterFrame = requestAnimationFrame(() => {
        this.counterFrame = null;
        const items = this.grid.children;
        const limit = window.innerHeight * 0.75;
        let low = 0, high = items.length - 1, index = 0;
        while (low <= high) {
          const middle = (low + high) >> 1;
          if (items[middle].getBoundingClientRect().top < limit) { index = middle + 1; low = middle + 1; } else high = middle - 1;
        }
        const output = counter.querySelector('[data-position-current]');
        if (!index || !output || items[0].getBoundingClientRect().top > limit) { counter.hidden = true; return; }
        output.textContent = String(index);
        counter.hidden = false;
        clearTimeout(this.counterTimer);
        this.counterTimer = setTimeout(() => { counter.hidden = true; }, 1500);
      });
    }
  }

  customElements.define('plp-root', PlpRoot);
})();
